import { RECEIVED_READ_MSG, type ReceivedDocument, type ReceivedPages, type ReceivedText } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api, messageOf } from "../../api.js";
import { PageViewer, ZOOM_MAX, ZOOM_MIN, type Zoom } from "../../components/PageViewer.jsx";

export { ZOOM_MAX, ZOOM_MIN, type Zoom };

/**
 * 読み取りの確認の引き出しの原本（受け取った書類 S3＝../eigyouryoku/docs/13 §4.5 の 4、依頼文 R3 §3-2・§3-3。絵＝received-read/Drawer・Parts ⑤）。
 *   どの幅でもページ画像（PC の PDF の <iframe> をやめた＝ブラウザの小さな PDF 表示に頼らない）。GET /received-documents/:id/pages を縦に並べる。文字の書類は <pre>
 *   ページの画像の見せ方（倍率・幅に合わせる・P.n／m・⌘ホイール・ダブルクリック・2本指）は components/PageViewer.tsx（#198 で外注見積の詳細の画面と共有。2026-10-17）
 *   「幅に合わせる」と extra の「原本を広く／内容を広く」は線画のアイコン（S4）。「原本表示」は文字のまま
 *   compact（幅 767px 以下）＝ツールバーは −・倍率・＋・幅に合わせる だけ（P.n／m は「原本 ›」の行に、原本を開くは ⋮ に）
 */
export function OriginalPane({
  doc: d,
  zoom,
  onZoom,
  compact = false,
  extra,
  onPage,
}: {
  doc: ReceivedDocument;
  zoom: Zoom;
  onZoom: (z: Zoom) => void;
  compact?: boolean;
  /** ツールバーの右（「原本表示」の左）に置くもの＝境の切替「原本を広く／内容を広く」 */
  extra?: ReactNode;
  /** 今のページ（スクロールで変わる）と全部のページ数 */
  onPage?: (n: number, m: number) => void;
}) {
  const id = d.id;
  const isText = d.format === "text";
  const pages = useQuery({
    queryKey: ["received-pages", id],
    queryFn: () => api<ReceivedPages>("GET", `/received-documents/${id}/pages`),
    enabled: !isText,
    gcTime: 0,
  });
  const text = useQuery({
    queryKey: ["received-text", id],
    queryFn: () => api<ReceivedText>("GET", `/received-documents/${id}/text`),
    enabled: isText,
  });
  if (!isText) {
    const canOpen = !!d.originalUrl && d.contentType !== "image/tiff";
    return (
      <PageViewer
        docKey={id}
        title={d.title}
        pages={pages.data?.pages}
        error={pages.isError ? pages.error : undefined}
        pageCount={d.pageCount}
        openUrl={canOpen ? d.originalUrl : null}
        zoom={zoom}
        onZoom={onZoom}
        compact={compact}
        extra={extra}
        {...(onPage ? { onPage } : {})}
      />
    );
  }
  // 文字の書類：倍率なし。ツールバーは extra と（原本は文字なので）開く口なし
  return (
    <div className="rdp" data-testid="received-original">
      <div className="rdp-bar" role="toolbar" aria-label={RECEIVED_READ_MSG.original} data-testid="rd-toolbar">
        <span className="sp" />
        {compact ? null : extra}
      </div>
      <div className="rdp-scroll">
        {text.isError ? <p className="shu">{messageOf(text.error)}</p> : <pre className="rdp-text" data-testid="received-view">{text.data?.body ?? ""}</pre>}
      </div>
    </div>
  );
}
